import { isFiatMarketCode } from "@/utils/fiatMarket";

const STABLE_CODES = new Set(["USDT", "USDC"]);

function normalizeCode(code) {
  return String(code || "").toUpperCase();
}

/** 稳定币：code 小写后 usdt / usdc */
export function isStableCoin(code) {
  return STABLE_CODES.has(String(code || "").toUpperCase());
}

/** USDT↔USDC 等稳定互兑 */
export function isStableToStablePair(fromCode, toCode) {
  const from = normalizeCode(fromCode);
  const to = normalizeCode(toCode);
  return isStableCoin(from) && isStableCoin(to) && from !== to;
}

/**
 * v2.5 convertType 1–7
 * from=法币 → to 稳定=2 / 其它加密=1
 * from=稳定 → to 法币=3 / to 稳定=7 / 其它加密=4
 * from=其它加密 → to 法币=5 / to 稳定=6
 */
export function resolveConvertType(fromCode, toCode) {
  const from = String(fromCode || "").toUpperCase();
  const to = String(toCode || "").toUpperCase();
  if (!from || !to) return null;

  if (isFiatMarketCode(from)) {
    return isStableCoin(to) ? 2 : 1;
  }
  if (isStableCoin(from)) {
    if (isFiatMarketCode(to)) return 3;
    if (isStableCoin(to)) return 7;
    return 4;
  }
  return isFiatMarketCode(to) ? 5 : 6;
}

/** 优先使用 config/list 返回的 type（覆盖 VND 等无交易对法币）；无配置时回退本地推断 */
export function resolveConvertTypeForPair(fromCode, toCode, configs = []) {
  const config = findConfigForPair(configs, fromCode, toCode);
  if (config?.type != null) {
    const type = Number(config.type);
    if (Number.isFinite(type)) return type;
  }
  return resolveConvertType(fromCode, toCode);
}

/** 由 from/to 币种类型判定业务方向（下单统一走 POST /convert/order） */
export function resolveConvertMode(fromCode, toCode) {
  if (!fromCode || !toCode) return "invalid";
  const from = String(fromCode).toUpperCase();
  const to = String(toCode).toUpperCase();
  if (from === to) return "invalid";

  const fromFiat = isFiatMarketCode(from);
  const toFiat = isFiatMarketCode(to);
  if (fromFiat && toFiat) return "invalid";

  if (fromFiat && !toFiat) return "buy";
  if (!fromFiat && toFiat) return "sell";
  return "swap";
}

export function resolveCurrencyId(code, currencyOptionMap, configs = []) {
  const upper = String(code || "").toUpperCase();
  if (!upper) return null;

  const opt = currencyOptionMap?.get?.(upper);
  if (opt?.currencyId != null) return opt.currencyId;
  if (opt?.cryptoCurrencyId != null) return opt.cryptoCurrencyId;

  for (const c of configs) {
    const cfgFrom = String(c.currencyCode || "").toUpperCase();
    const cfgTo = String(c.cryptoCurrencyCode || "").toUpperCase();
    if (cfgFrom === upper && c.currencyId != null) return c.currencyId;
    if (cfgTo === upper && c.cryptoCurrencyId != null) return c.cryptoCurrencyId;
  }
  return null;
}

/**
 * v2 配置：currencyCode=from，cryptoCurrencyCode=to；兼容旧 type=1/2/3 存储
 */
export function findConfigForPair(configs, fromCode, toCode) {
  const from = String(fromCode || "").toUpperCase();
  const to = String(toCode || "").toUpperCase();
  if (!from || !to) return null;

  const v2Match = configs.find(
    (c) =>
      String(c.currencyCode || "").toUpperCase() === from &&
      String(c.cryptoCurrencyCode || "").toUpperCase() === to
  );
  if (v2Match) return v2Match;

  const legacyBuy = configs.find(
    (c) =>
      Number(c.type) === 1 &&
      String(c.currencyCode || "").toUpperCase() === from &&
      String(c.cryptoCurrencyCode || "").toUpperCase() === to
  );
  if (legacyBuy) return legacyBuy;

  const legacySell = configs.find(
    (c) =>
      Number(c.type) === 2 &&
      String(c.currencyCode || "").toUpperCase() === to &&
      String(c.cryptoCurrencyCode || "").toUpperCase() === from
  );
  if (legacySell) return legacySell;

  const legacySwapDirect = configs.find(
    (c) =>
      Number(c.type) === 3 &&
      String(c.currencyCode || "").toUpperCase() === from &&
      String(c.cryptoCurrencyCode || "").toUpperCase() === to
  );
  if (legacySwapDirect) return legacySwapDirect;

  const legacySwapReverse = configs.find(
    (c) =>
      Number(c.type) === 3 &&
      String(c.currencyCode || "").toUpperCase() === to &&
      String(c.cryptoCurrencyCode || "").toUpperCase() === from
  );
  if (legacySwapReverse) return legacySwapReverse;

  return null;
}

/** 从 config/list 汇总可作为 From 的币种（v2：currencyCode = from） */
export function resolveFromCodesFromConfigs(configs = []) {
  const fromSet = new Set();
  configs.forEach((c) => {
    const fromLeg = normalizeCode(c.currencyCode);
    if (fromLeg) fromSet.add(fromLeg);
  });
  return [...fromSet];
}

/** 选定 From 后，从配置中汇总可选 To（v2：cryptoCurrencyCode = to） */
export function resolveToCodesForFrom(fromCode, configs = []) {
  const from = normalizeCode(fromCode);
  if (!from) return [];

  const toSet = new Set();
  configs.forEach((c) => {
    const fromLeg = normalizeCode(c.currencyCode);
    const toLeg = normalizeCode(c.cryptoCurrencyCode);
    if (from === fromLeg && toLeg && toLeg !== from) toSet.add(toLeg);
  });
  return [...toSet];
}

/** 根据 config type 推断某 code 是否为法币腿（补充 plate 258 未覆盖的配置法币） */
export function inferIsFiatFromConfigs(code, configs = []) {
  const upper = normalizeCode(code);
  if (!upper) return false;
  if (isStableCoin(upper)) return false;
  if (isFiatMarketCode(upper)) return true;

  return configs.some((c) => {
    const type = Number(c.type);
    const fromLeg = normalizeCode(c.currencyCode);
    const toLeg = normalizeCode(c.cryptoCurrencyCode);
    if (type === 1 && fromLeg === upper) return true;
    if ([2, 3, 5].includes(type) && toLeg === upper) return true;
    if (type === 2 && fromLeg === upper && !isStableCoin(fromLeg)) return true;
    return false;
  });
}

/** preview：currencyId=from，cryptoCurrencyId=to，type 取自 config/list */
export function buildPreviewPayload(fromCode, toCode, sourceAmount, currencyOptionMap, configs) {
  return {
    type: resolveConvertTypeForPair(fromCode, toCode, configs),
    currencyId: resolveCurrencyId(fromCode, currencyOptionMap, configs),
    cryptoCurrencyId: resolveCurrencyId(toCode, currencyOptionMap, configs),
    sourceAmount,
  };
}

/**
 * v2.5 通用下单 body（POST /convert/order）
 * preview.currencyId → fromCurrencyId；preview.cryptoCurrencyId → toCurrencyId
 */
export function buildUniversalOrderPayload(fromCode, toCode, sourceAmount, currencyOptionMap, configs) {
  return {
    fromCurrencyId: resolveCurrencyId(fromCode, currencyOptionMap, configs),
    toCurrencyId: resolveCurrencyId(toCode, currencyOptionMap, configs),
    sourceAmount,
  };
}

const CONVERT_PREVIEW_ERROR_MC_MAP = {
  PAYMENT_CONVERT_1001: "convert.errorAmountTooSmallAfterFees",
  "Convert amount too small: net amount after fees is zero or negative":
    "convert.errorAmountTooSmallAfterFees",
  CONVERT_AMOUNT_TOO_SMALL: "convert.errorAmountTooSmallAfterFees",
};

/** preview 接口 mc → 前端 i18n key */
export function resolveConvertPreviewErrorKey(mc) {
  const raw = String(mc || "").trim();
  if (!raw) return null;
  return CONVERT_PREVIEW_ERROR_MC_MAP[raw] || null;
}

export function resolvePairErrorKey(fromCode, toCode, convertMode, hasConfig) {
  if (!fromCode || !toCode) return null;
  const from = String(fromCode).toUpperCase();
  const to = String(toCode).toUpperCase();
  if (from === to) return "convert.errorInvalidPair";
  if (isFiatMarketCode(from) && isFiatMarketCode(to)) return "convert.errorFiatToFiat";
  if (convertMode === "invalid" || !hasConfig) return "convert.errorInvalidPair";
  return null;
}
